import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  VoiceChat,
  Mic,
  MicOff,
  PlayArrow,
  Stop,
  Upload,
  VolumeUp,
  ArrowBack,
  PostAdd
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { voiceAPI } from '../services/voiceAPI';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`voice-tabpanel-${index}`}
      aria-labelledby={`voice-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const VoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [textToSpeak, setTextToSpeak] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        handleTranscription(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
      setError(null);
    } catch (err) {
      setError('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const handleTranscription = async (audioBlob: Blob) => {
    setTranscribing(true);
    try {
      // Convert Blob to File
      const audioFile = new File([audioBlob], 'recording.wav', { type: 'audio/wav' });
      
      const response = await voiceAPI.voiceToText({
        file: audioFile,
        language_code: 'en-US'
      });
      setTranscribedText(response.data?.text || '');
    } catch (err: any) {
      setError('Failed to transcribe audio');
      console.error('Transcription error:', err);
    } finally {
      setTranscribing(false);
    }
  };

  const handleTextToSpeech = async () => {
    if (!textToSpeak.trim()) return;

    setGenerating(true);
    try {
      const response = await voiceAPI.textToSpeech({
        text: textToSpeak,
        voice: 'en-US-Neural2-F',
        language: 'en-US'
      });
      setAudioUrl(response.data?.audio_url || '');
    } catch (err: any) {
      setError('Failed to generate speech');
      console.error('Text-to-speech error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const generatePostFromVoice = async () => {
    if (!transcribedText.trim()) return;

    try {
      // For voice to post, we need to create a new audio file with the transcribed text as topic
      const dummyFile = new File([''], 'dummy.wav', { type: 'audio/wav' });
      
      const response = await voiceAPI.voiceToPost({
        file: dummyFile,
        language_code: 'en-US',
        topic: transcribedText
      });
      
      // Navigate to create post with the generated content
      navigate('/create-post', {
        state: {
          topic: 'Voice-generated content',
          content: response.data?.generated_post || transcribedText
        }
      });
    } catch (err: any) {
      setError('Failed to generate post from voice');
      console.error('Voice-to-post error:', err);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <VoiceChat color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Voice Processing
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Record audio, transcribe speech, and convert text to speech
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Voice to Text" />
          <Tab label="Text to Speech" />
          <Tab label="Voice to Post" />
        </Tabs>

        {/* Voice to Text Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Record Audio
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <IconButton
                      size="large"
                      color={recording ? 'secondary' : 'primary'}
                      onClick={recording ? stopRecording : startRecording}
                      sx={{
                        fontSize: 60,
                        width: 120,
                        height: 120,
                        border: 3,
                        borderStyle: 'solid',
                        animation: recording ? 'pulse 1.5s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }}
                    >
                      {recording ? <MicOff /> : <Mic />}
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {recording ? 'Recording... Click to stop' : 'Click to start recording'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Transcription
                  </Typography>
                  {transcribing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
                      <CircularProgress sx={{ mr: 2 }} />
                      <Typography>Transcribing audio...</Typography>
                    </Box>
                  ) : transcribedText ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={transcribedText}
                        onChange={(e) => setTranscribedText(e.target.value)}
                        placeholder="Transcribed text will appear here..."
                      />
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<PostAdd />}
                          onClick={generatePostFromVoice}
                        >
                          Generate Post
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setTranscribedText('')}
                        >
                          Clear
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Record audio to see transcription here
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Text to Speech Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Enter Text to Convert
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={textToSpeak}
                    onChange={(e) => setTextToSpeak(e.target.value)}
                    placeholder="Enter text to convert to speech..."
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={16} /> : <VolumeUp />}
                    onClick={handleTextToSpeech}
                    disabled={!textToSpeak.trim() || generating}
                  >
                    {generating ? 'Generating...' : 'Generate Speech'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generated Audio
                  </Typography>
                  {audioUrl ? (
                    <Box>
                      <audio controls style={{ width: '100%' }}>
                        <source src={audioUrl} type="audio/wav" />
                        Your browser does not support the audio element.
                      </audio>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Click play to listen to generated speech
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Generate speech to see audio player here
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Voice to Post Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Voice to Social Media Post
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Record your thoughts and automatically generate optimized social media content
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <IconButton
                      size="large"
                      color={recording ? 'secondary' : 'primary'}
                      onClick={recording ? stopRecording : startRecording}
                      sx={{
                        fontSize: 40,
                        width: 80,
                        height: 80,
                        border: 2,
                        borderStyle: 'solid'
                      }}
                    >
                      {recording ? <MicOff /> : <Mic />}
                    </IconButton>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {recording ? 'Recording your voice...' : 'Start recording'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  {transcribedText && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Transcribed Text:
                      </Typography>
                      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">
                          {transcribedText}
                        </Typography>
                      </Paper>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<PostAdd />}
                        onClick={generatePostFromVoice}
                      >
                        Create Social Media Post
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* Features List */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Voice Processing Features:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Real-time Transcription"
                      secondary="Convert speech to text with high accuracy"
                    />
                    <Chip label="AI Powered" size="small" color="primary" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Content Optimization"
                      secondary="Automatically optimize content for social media platforms"
                    />
                    <Chip label="Smart" size="small" color="success" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Multiple Languages"
                      secondary="Support for various languages and accents"
                    />
                    <Chip label="Multilingual" size="small" color="info" />
                  </ListItem>
                </List>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default VoicePage; 